import React, { useEffect, useState } from "react";
import {
  customFilter,
  selectFilter,
  textFilter,
} from "react-bootstrap-table2-filter";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import { Badge, Card, CardBody, Col, Row } from "reactstrap";
import {
  notificationIsReadFilter,
  getInitNotificationParams,
  notificationAuthStatus,
} from "./utils";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import Breadcrumbs from "components/Common/Breadcrumb";
import DetailNotification from "./DetailNotification";
import {
  CUSTOM_DATE_FILTER_TABLE_TYPE,
  CUSTOM_SELECT_FILTER_TABLE_TYPE,
  DELAY_FILTER_TIME,
} from "constants/RemoteDataTableContanst";
import CustomDateFilter from "components/shared-components/RemoteTable/custom-date-filter";
import { actionAccess } from "../../constants/actionAccess";
import Select from "react-select";
import BadgeForTable from "components/shared-components/BadgeForTable";
import { useNotificationList, usePopupNotificationList, useReadNotification } from "hooks";
import { setNotificationListColumn } from "store/actions";

const ListNotification = props => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation(["notificationManagement"]);
  const { addToast } = useToasts();
  const navigate = useNavigate();

  const { list: notificationListStore } = useSelector(
    state => state["notification"]
  );

  /* ================================== START INIT STATE ==================================== */

  const [columnsTable, setColumnsTable] = useState([]);
  const [notificationParams, setNotificationParams] = useState(
    getInitNotificationParams
  );
  const [selectedIsRead, setSelectedIsRead] = useState(notificationIsReadFilter[0])

  /* =================================== END INIT STATE ===================================== */

  /* ===================================== START HOOKS ====================================== */

  // NOTIFICATION LIST

  const {
    data: notificationList,
    refetch: fetchNotificationList,
    isFetching: isLoadingNotificationList,
  } = useNotificationList({ params: notificationParams });

  // NOTIFICATION POPUP

  const {
    refetch: fetchPopupNotificationList,
  } = usePopupNotificationList();

  // READ NOTIFICATION

  const onReadNotificationSuccess = () => {
    fetchPopupNotificationList();
  }

  const { mutate: readNotification } = useReadNotification(
    onReadNotificationSuccess
  );

  useEffect(() => {
    if (notificationParams && notificationParams.run) {
      fetchNotificationList();
    }
    initColumn()
  }, [notificationParams]);

  useEffect(() => {
    setNotificationParams(prev => ({
      ...prev,
      page: 1,
      sizePerPage: 10,
      run: true,
      columnFilter: { "filter[auth_status]": "M" }
    }));

    initColumn();
  }, [])

  useEffect(() => {
    if (notificationListStore.columnsVisible) {
      initColumn();
    }
  }, [notificationListStore, notificationParams])

  /* ====================================== END HOOKS ======================================= */

  /* ================================ START FUNCTION HANDLER ================================ */

  const initColumn = () => {
    if (notificationListStore.columnsVisible) {
      for (const item of initColumns) {
        if (
          notificationListStore.columnsVisible[item.dataField] !== undefined
        ) {
          item.hidden = !notificationListStore.columnsVisible[item.dataField];
        }
      }
    }
    setColumnsTable(initColumns);
  };

  const handleTableChange = (
    type,
    { page, sizePerPage, sortField, sortOrder, columnFilter }
  ) => {
    setNotificationParams(prev => ({
      ...prev,
      sort: sortField ? sortOrder + sortField : notificationParams.sort,
      columnFilter: columnFilter || {},
      page: page || 1,
      sizePerPage: sizePerPage || 10,
    }));
  };

  const onSearchAllHandle = value => {
    setNotificationParams(prev => ({
      ...prev,
      keywords: value,
    }));
  };

  const optionShowColumnHandler = (field, value) => {
    dispatch(setNotificationListColumn(field, value));
  };

  const rowEventsHandler = {
    onDoubleClick: (_, row) => {
      
    },
  };

  const formatOptionsTable = (cell, name = "", prefix = "") => {
    let options = [];
    switch (name) {
      case "auth_status":
        if (prefix === "notification") {
          options = notificationAuthStatus;
        }
        break;
      default:
        return "";
    }

    return options.map(
      item =>
        item.key === cell && (
          <BadgeForTable
            key={item.key}
            badgeColor={item.badgeColor ? item.badgeColor : ""}
          >
            {selectOptionsTable(cell, name, prefix)}
          </BadgeForTable>
        )
    );
  };

  const selectOptionsTable = (cell = undefined, name = "", prefix = "") => {
    let options = [];
    const select = {};
    switch (name) {
      case "auth_status":
        if (prefix === "notification") {
          options = notificationAuthStatus;
        }
        break;
      default:
        if (cell) {
          return "";
        } else {
          return select;
        }
    }

    options.forEach(item => {
      select[item.key] = t(`table.${name}.${prefix}.${item.key}`);
    });

    if (cell) {
      return select[cell];
    }

    return { ...select };
  };

  const onReadNotification = (row) => {
    readNotification(row.notification_id)
    navigate(`/manage-orders/${row.notification.order_id}`)
  }

  /* ================================= END FUNCTION HANDLER ================================= */

  const listRowAction = [
    {
      actionAccess: actionAccess.GLOG_FUNCTION_NOTIFICATION_VIEW,
      icon: <i className="fas fa-eye fa-lg text-primary ms-2" />,
      text: t("actions.view"),
      onClick: onReadNotification,
    },
  ];

  const listMultipleRowAction = [];

  const initColumns = [
    {
      dataField: "notification.title",
      text: t("table.title"),
      sort: true,
      filter: textFilter({
        delay: DELAY_FILTER_TIME,
        placeholder: t("placeholder.enter", {
          title: t("table.title"),
        }),
      }),
    },
    {
      dataField: "notification.description",
      text: t("table.description"),
      sort: true,
      filter: textFilter({
        delay: DELAY_FILTER_TIME,
        placeholder: t("placeholder.enter", {
          title: t("table.description"),
        }),
      }),
    },
    {
      dataField: "maker_date",
      text: t("table.maker_date"),
      sort: true,
      formatter: (cell, row) => {
        if (cell) {
          return new Intl.DateTimeFormat("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }).format(Number(cell) * 1000);
        } else {
          return "";
        }
      },
      filter: customFilter({
        type: CUSTOM_DATE_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => (
        <CustomDateFilter
          onFilter={onFilter}
          column={column}
          paramFilter={notificationParams.columnFilter}
        />
      ),
    },
    {
      dataField: "auth_status",
      text: t("table.auth_status.notification.name"),
      sort: false,
      align: "center",
      allowExport: true,
      filter: selectFilter({
        type: CUSTOM_SELECT_FILTER_TABLE_TYPE,
      }),
      filterRenderer: (onFilter, column) => {
        return (
          <div style={{ minWidth: "200px", fontWeight: "normal" }}>
            <Select
              isClearable
              placeholder={t("placeholder.select", {
                title: t("table.auth_status.notification.name"),
              })}
              options={notificationIsReadFilter || []}
              classNamePrefix="select2-selection"
              value={selectedIsRead}
              onChange={data => {
                onFilter({ data: data });
                setSelectedIsRead(data)
              }}
            />
          </div>
        );
      },
      formatter: (cell, row) => {
        const hasNotify = notificationAuthStatus.some(item => item.key === cell);
        if (cell && hasNotify) {
          return formatOptionsTable(cell, "auth_status", "notification");
        } else {
          return "";
        }
      },
    },
  ];

  return (
    <React.Fragment>
      <div className="page-content badge-soft ">
        <MetaTags>
          <title>{t("title.list")}</title>
        </MetaTags>
        <div className="container-fluid">
          <Breadcrumbs
            title={t("title.manager")}
            breadcrumbItem={t("title.list") + ` (${notificationList?.totalCount || 0})`}
          />
          <Row>
            {/* Modal START*/}
            <Col className="col-12">
              {/* Detail Notification Modal */}
              {/* <DetailNotification
                dataDetail={dataDetail}
                isOpenModal={detailModal}
                toggleModal={toggleDetailModal}
                mode={mode}
                refreshNotification={refreshNotification}
              /> */}
            </Col>
            {/* Modal E N D*/}
            <Col className="col-12">
              <Card>
                <CardBody>
                  <RemoteDataTable
                    keyField="id"
                    data={notificationList?.items || []}
                    columns={columnsTable}
                    page={notificationParams.page}
                    sizePerPage={notificationParams.sizePerPage}
                    totalSize={notificationList?.totalCount || 0}
                    onTableChange={handleTableChange}
                    onSearchAllHandle={onSearchAllHandle}
                    optionShowColumnHandel={optionShowColumnHandler}
                    rowEvents={rowEventsHandler}
                    listMultipleRowAction={listMultipleRowAction}
                    listRowAction={listRowAction}
                    loading={isLoadingNotificationList}
                  ></RemoteDataTable>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default ListNotification;
